import React, { useEffect } from "react"
import { View } from "react-native"
import { connect } from "react-redux"
import { useNavigation, useRoute } from "@react-navigation/native"
import { ActivityIndicator } from "react-native-paper"
import * as amplitude from "@amplitude/analytics-react-native"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextButtonM from "@common/components/button/TextButtonM"
import { theme } from "@common/theme/theme"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiRequest } from "@data/redux/actions/api"
import { normaliseClass } from "../summer/constants/summerContent"
import { inscriptionFunnelService } from "./services/inscriptionFunnelService"
import { FORMAT_STEP } from "./constants/inscriptionFormats"
import { useInscriptionFunnel } from "./context/InscriptionFunnelContext"
import { resumedWithCommitment } from "./utils/formulaChoice"
import { isUnreadableResumeLink } from "@common/utils/funnelLinkIdentity"
import { useResumeFailureReport } from "@common/hooks/useResumeFailureReport"
import {
    clearUserFromUrl,
    readUserParam,
} from "./hooks/useInscriptionFunnelGuard"

/**
 * Atterrissage du lien magique : GET /inscription/resume?user=<base64email>.
 *
 * Appelle l'endpoint de reprise (derrière `auth_or_email`), hydrate le Context
 * depuis l'état FastTrack persisté, puis navigation.reset() vers l'étape
 * correspondant à l'état. Jumeau des écrans de reprise été et stage.
 */

// Chaque état → la pile complète, pour que goBack() remonte nativement les
// étapes précédentes. La DERNIÈRE entrée est l'étape de reprise.
//
// Les états PAYÉS atterrissent sur la confirmation du funnel — et non sur le
// formulaire de disponibilités : les créneaux sont déjà collectés à l'étape
// configuration, redemander revient à ignorer ce que le parent vient de saisir.
//
// `null` couvre un utilisateur sans état d'inscription (orphelin / autre funnel) :
// il atterrit sur inscription/start avec l'identité hydratée par /resume. Un
// dossier sans classe connue y retourne aussi — voir la résolution de la pile.
const STATE_TO_STACK = {
    inscription_started: ["inscription/start", "inscription/diagnostic"],
    // Diagnostic bouclé → le parent reprend sur la configuration de sa formule.
    inscription_diag_completed: [
        "inscription/start",
        "inscription/diagnostic",
        "inscription/configuration",
    ],
    // Checkout déjà ouvert (ou paiement échoué) : la formule est composée, on
    // le repose sur les tuteurs, à un pas du paiement.
    inscription_checkout_created: [
        "inscription/start",
        "inscription/diagnostic",
        "inscription/configuration",
        "inscription/tutors",
    ],
    inscription_failed: [
        "inscription/start",
        "inscription/diagnostic",
        "inscription/configuration",
        "inscription/tutors",
    ],
    inscription_paid: ["inscription/confirmation"],
    inscription_attribution_pending: ["inscription/confirmation"],
    null: ["inscription/start"],
}

/**
 * Un dossier qui choisit son format sur sa propre page la retrouve dans sa
 * pile, juste avant les tuteurs : « retour » depuis les tuteurs doit y mener,
 * la page de la formule ne posant plus la question.
 */
const withFormatStep = (stack, formatStep) =>
    formatStep === FORMAT_STEP.ownStep
        ? stack.flatMap((name) =>
              name === "inscription/tutors"
                  ? ["inscription/format", name]
                  : [name]
          )
        : stack

const TERMINAL_PAID_STATES = [
    "inscription_paid",
    "inscription_attribution_pending",
]

/**
 * Couture de normalisation UNIQUE du funnel d'inscription : borne les types et
 * backfille depuis la source la plus fiable, pour que la reprise marche quelle
 * que soit la complétude du checkout_params.
 */
const mapStateCheckoutParamsToContext = (
    state,
    checkoutParams,
    user,
    quote
) => {
    const cp = checkoutParams || {}
    return {
        funnelStateId: state.funnel_state_id || null,
        formatStep: state.format_step || null,
        // Le prénom LIVE (corrigé au BO) prime sur le snapshot figé.
        studentFirstname: user?.firstname || cp.firstname || "",
        parentEmail: user?.email || "",
        submissionGUID: cp.submission_guid || null,
        leadIntent: cp.lead_intent || null,
        studentClass:
            normaliseClass(cp.student_class || user?.student_class) || null,
        besoinsSpeciaux: Array.isArray(cp.besoins_speciaux)
            ? cp.besoins_speciaux
            : [],
        // Ids en STRING dès l'hydratation : les chips comparent des strings,
        // le service remet des int sur le wire.
        subjects: (Array.isArray(cp.subjects) ? cp.subjects : []).map(String),
        // Le cran visé par matière, tel que le parent l'a placé — sans lui, la
        // reprise remettrait chaque curseur au défaut.
        subjectGoals:
            cp.subject_goals && typeof cp.subject_goals === "object"
                ? { ...cp.subject_goals }
                : {},
        offerType: cp.offer_type || null,
        weeklyHours: Number(cp.weekly_hours) || 0,
        // Les créneaux sont relus dans leur forme LOCALE — c'est celle que la
        // grille sait recocher ; la forme canonique ne sert qu'au serveur.
        slots: Array.isArray(cp.slots_local) ? cp.slots_local : [],
        hoursAllocation: cp.hours_allocation || {},
        startDate: cp.start_date || null,
        withCommitment: resumedWithCommitment(cp, quote),
        // Les bornes de date et la FAQ ne passent PAS par ici : le reducer les
        // prend sur la même réponse et les range dans le store, seul domicile
        // de la config du funnel. Elles sont recalculées côté serveur à CHAQUE
        // reprise — une reprise tardive ne rejoue pas un plancher périmé.
        //
        // Le devis, lui, appartient au parcours : recalculé tant que la
        // réservation n'est pas payée, GRAVÉ une fois encaissée (le serveur
        // rend alors le devis vendu, pas un recalcul).
        quote: quote || null,
        paid: TERMINAL_PAID_STATES.includes(state.state),
    }
}

const InscriptionResumeScreen = (props) => {
    const navigation = useNavigation()
    const route = useRoute()
    const { update, reset } = useInscriptionFunnel()

    const userParam = readUserParam(route)

    useEffect(() => {
        if (!userParam) return
        // Reset first so the Context doesn't keep stale values from a previous
        // lead sharing the same browser tab (admin-on-laptop flow).
        reset()
        props.apiRequest(
            inscriptionFunnelService.resumeInscription,
            {},
            {},
            { user: userParam }
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userParam])

    useApiStatus(
        inscriptionFunnelService.resumeInscription,
        null,
        false,
        (data) => {
            if (!data) return

            const stateName = data.state
            // `resumedToken` voyage dans le MÊME patch que l'hydratation : la
            // garde du funnel saute la reprise pour un jeton déjà arbitré. Sans
            // lui, un lead SANS dossier (state null → funnelStateId null) était
            // renvoyé de start vers resume en boucle infinie (React #185).
            update({
                ...mapStateCheckoutParamsToContext(
                    data,
                    data.checkout_params,
                    data.user,
                    data.quote
                ),
                resumedToken: userParam,
            })

            if (stateName === null) {
                amplitude.track("inscription_resume_fresh_start", {
                    has_user: !!data.user?.email,
                    has_student_class: !!data.user?.student_class,
                })
            }

            // Un dossier peut exister SANS classe connue : la reprise l'ouvre pour
            // un lead venu d'un autre funnel, et le profil BO n'en porte pas
            // toujours. Le laisser filer vers le diagnostic le mènerait au
            // configurateur sans catalogue de matières, bloqué sans motif — la
            // garde du diagnostic se tait quand un `?user=` est présent.
            const stack = withFormatStep(
                !data.checkout_params?.student_class
                    ? ["inscription/start"]
                    : STATE_TO_STACK[
                          stateName === null ? "null" : stateName
                      ] || ["inscription/start"],
                data.format_step
            )
            // Carry `user` onto EVERY route of the rebuilt stack — a goBack() onto
            // an intermediate step (or a refresh while on it) must still find the
            // `?user=` token to stay shareable and refresh-resumable.
            navigation.reset({
                index: stack.length - 1,
                routes: stack.map((name) => ({
                    name,
                    params: { user: userParam },
                })),
            })
        }
    )

    // Un échec (throttling, 500, réseau coupé, lien mort) se traite SUR PLACE :
    // naviguer vers le début du funnel remettait le parent face à la garde, qui
    // le renvoyait ici — l'aller-retour bouclait jusqu'au crash. Même idiome
    // que le jumeau été (SummerResumeScreen).
    const retry = () => {
        props.apiRequest(
            inscriptionFunnelService.resumeInscription,
            {},
            {},
            { user: userParam }
        )
    }

    // Un lien que le serveur ne reconnaît pas (jeton abîmé en route, adresse
    // inconnue) et une panne passagère méritent deux écrans différents :
    // proposer « Réessayer » sur un lien illisible envoie le parent sur un
    // bouton qui ne peut jamais aboutir. Même distinction que la landing
    // d'activation.
    const unreadableLink = isUnreadableResumeLink(userParam, props.error)

    useResumeFailureReport("inscription", props.error, {
        unreadable: unreadableLink,
    })

    if (!userParam || props.error) {
        return (
            <View style={styles.root}>
                <TextM
                    color={theme.colors.error}
                    wrap
                    style={styles.errorText}
                    fontWeight="Bold"
                    fontSize={14}
                >
                    {unreadableLink
                        ? "Ce lien n'est plus valide"
                        : "Impossible de reprendre votre inscription pour le moment."}
                </TextM>
                {!!userParam && !unreadableLink && (
                    <FilledButtonM onPress={retry} label="Réessayer" />
                )}
                <TextButtonM
                    onPress={() => {
                        // Sortie volontaire du parcours repris : sans le
                        // nettoyage, la garde relit `?user=` dans l'URL web
                        // et renvoie ici.
                        clearUserFromUrl()
                        navigation.replace("inscription/start")
                    }}
                    label="Reprendre depuis le début"
                />
            </View>
        )
    }

    return (
        <View style={styles.root}>
            <ActivityIndicator color={theme.colors.primary} />
            <TextM style={styles.label}>Reprise de votre inscription…</TextM>
        </View>
    )
}

const styles = {
    root: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        backgroundColor: theme.colors.white,
    },
    label: {
        fontSize: 14,
        color: theme.colors.grey,
    },
    errorText: {
        textAlign: "center",
        paddingHorizontal: 20,
    },
}

const mapStateToProps = (state) => ({
    error: state.api?.resumeInscription?.error,
})

export default connect(mapStateToProps, { apiRequest })(InscriptionResumeScreen)
